import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import {
  calculateMultiPortfolioGoalValue,
  calculateGoalStatus,
  calculateProgressPercentage,
  calculateGoalProjection,
  calculateGoalMonthlyContribution,
} from '../../utils/calculations';
import {
  formatCurrency,
  formatDate,
  getStatusColor,
} from '../../utils/formatters';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import GoalModal from './GoalModal';

const Goals = () => {
  const navigate = useNavigate();
  const { portfolios, goals, transactions, exchangeRates, deleteGoal } = useData();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);

  const handleEdit = (e, goal) => {
    e.stopPropagation();
    setEditingGoal(goal);
    setIsModalVisible(true);
  };

  const handleDelete = () => {
    if (goalToDelete) {
      deleteGoal(goalToDelete.id);
      setDeleteDialogOpen(false);
      setGoalToDelete(null);
    }
  };

  const handleModalClose = () => {
    setEditingGoal(null);
    setIsModalVisible(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold m-0">Funds</h3>
        <button
          onClick={() => setIsModalVisible(true)}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add Fund</span>
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="mt-6">
          <div className="flex flex-col items-center gap-4">
            <p className="text-gray-600">No funds created yet</p>
            <button
              onClick={() => setIsModalVisible(true)}
              className="px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              Create Your First Fund
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.map((goal) => {
            const currentValue = calculateMultiPortfolioGoalValue(goal, portfolios, exchangeRates);
            const progressPercentage = calculateProgressPercentage(currentValue, goal.targetAmount);
            const status = calculateGoalStatus(goal, currentValue, portfolios, exchangeRates);
            const projectionResult = calculateGoalProjection(goal, portfolios, transactions, exchangeRates, null);
            const projection = projectionResult?.projection || [];
            const goalReachDate = projectionResult?.goalReachDate;
            const monthlyContribution = calculateGoalMonthlyContribution(goal, portfolios, exchangeRates);

            return (
              <div
                key={goal.id}
                className="bg-gray-50 rounded-lg border border-gray-200 p-4 cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/funds/${goal.id}`)}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex gap-3 items-start">
                    <div className="flex-1">
                      <div className="text-sm font-semibold">
                        {goal.name}
                      </div>
                      <span
                        className="inline-block text-xs text-white uppercase px-2 py-0.5 rounded mt-1"
                        style={{ backgroundColor: getStatusColor(status.status) }}
                      >
                        {status.status}
                      </span>
                    </div>
                  </div>

                  <div className="-mt-2">
                    <div className="flex justify-between mb-2 text-xs">
                      <span className="text-xs font-bold">{formatCurrency(currentValue, goal.currency, 0)}</span>
                      <span className="text-xs text-gray-600">of {formatCurrency(goal.targetAmount, goal.currency, 0)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, progressPercentage)}%`,
                          backgroundColor: goal.color
                        }}
                      />
                    </div>
                    <div className="text-center mt-1">
                      <span className="text-xs text-gray-600">{progressPercentage.toFixed(0)}% complete</span>
                    </div>
                  </div>

                  {/* Mini Projection Chart */}
                  {projection.length > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="mb-2">
                        <div className="text-xs font-semibold text-gray-700 mb-1">Projection</div>
                        {goalReachDate && (
                          <div className="text-xs text-blue-600">
                            Reaches goal: {goalReachDate.format('MMM YYYY')}
                          </div>
                        )}
                      </div>
                      <ResponsiveContainer width="100%" height={120}>
                        <ComposedChart data={projection} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id={`miniGradient-${goal.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={goal.color || '#c8865c'} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={goal.color || '#c8865c'} stopOpacity={0.05}/>
                            </linearGradient>
                          </defs>
                          <XAxis
                            dataKey="displayDate"
                            stroke="#9ca3af"
                            style={{ fontSize: '9px' }}
                            tick={{ fill: '#6b7280' }}
                            interval="preserveStartEnd"
                            tickCount={3}
                          />
                          <YAxis
                            stroke="#9ca3af"
                            style={{ fontSize: '9px' }}
                            tick={{ fill: '#6b7280' }}
                            tickFormatter={(value) => {
                              if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                              if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                              return value.toFixed(0);
                            }}
                            width={40}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: 6,
                              fontSize: '11px',
                              padding: '4px 8px',
                            }}
                            formatter={(value) => formatCurrency(value, goal.currency, 0)}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={goal.color || '#c8865c'}
                            strokeWidth={1.5}
                            fill={`url(#miniGradient-${goal.id})`}
                            fillOpacity={1}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke={goal.color || '#c8865c'}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                          {goal.targetAmount > 0 && (
                            <ReferenceLine
                              y={goal.targetAmount}
                              stroke="#b8754e"
                              strokeDasharray="3 3"
                              strokeWidth={1.5}
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="flex flex-col gap-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Target:</span>
                      <span>{formatDate(goal.targetDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly:</span>
                      <span className="font-medium">
                        {formatCurrency(monthlyContribution, goal.currency, 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Allocations:</span>
                      <span>{goal.allocations?.length || 0} account(s)</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-center gap-6">
                      <button
                        onClick={(e) => handleEdit(e, goal)}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                      >
                        <PencilIcon className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setGoalToDelete(goal);
                          setDeleteDialogOpen(true);
                        }}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                      >
                        <TrashIcon className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <GoalModal
        visible={isModalVisible}
        onCancel={handleModalClose}
        goal={editingGoal}
      />

      {/* Delete Confirmation Dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Delete this fund?
                    </Dialog.Title>
                  </div>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This will not delete associated transactions.
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setGoalToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      onClick={handleDelete}
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Goals;
